import React, { useState } from "react";
import { api, streamSSE } from "@/lib/api";
import Markdown from "@/components/Markdown";
import { toast } from "sonner";
import { Search, Loader2, Award, Beaker, ScrollText, Sparkles, Download, Copy, ShieldCheck, Pill } from "lucide-react";
import LabelLookupPanel from "@/components/LabelLookupPanel";

const TABS = [
  { code: "orange",   label: "Orange Book",  icon: Beaker,      hint: "Approved drugs + patents" },
  { code: "purple",   label: "Purple Book",  icon: ScrollText,  hint: "Biologics + biosimilars" },
  { code: "design",   label: "Designations", icon: Award,       hint: "Orphan / Breakthrough / FT / PR" },
  { code: "label",    label: "Label Lookup", icon: Pill,        hint: "DailyMed SPL + RxNorm" },
];

const DESIGS = [
  { key: "orphan",         label: "Orphan Drug" },
  { key: "breakthrough",   label: "Breakthrough Therapy" },
  { key: "fast_track",     label: "Fast Track" },
  { key: "priority_review",label: "Priority Review" },
  { key: "rmat",           label: "RMAT" },
];

const VERDICT_COLORS = {
  candidate:       "border-emerald-300 bg-emerald-50 text-emerald-800",
  weak_candidate:  "border-amber-300 bg-amber-50 text-amber-800",
  not_eligible:    "border-slate-300 bg-slate-100 text-slate-600",
};

export default function TherapeuticsPanel({ onAnchor }) {
  const [mode, setMode] = useState("orange");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);

  // Designation form
  const [df, setDf] = useState({ program_name: "", indication: "", unmet_need: "", prevalence: "", mechanism: "", preclinical_or_clinical: "" });
  const [designations, setDesignations] = useState(DESIGS.map((d) => d.key));
  const [designResults, setDesignResults] = useState(null);

  const runSearch = async () => {
    if (mode === "design") return runDesignation();
    if (!q.trim()) return;
    setLoading(true); setRows([]); setTotal(0);
    try {
      const path = mode === "orange" ? "/therapeutics/orange-book" : "/therapeutics/purple-book";
      const { data } = await api.get(path, { params: { q, limit: 25 } });
      setRows(data.results || []);
      setTotal(data.total || 0);
      if ((data.results || []).length === 0) toast.info("No results — try INN or brand name");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Search failed");
    } finally { setLoading(false); }
  };

  const runDesignation = async () => {
    if (!df.program_name || !df.indication) {
      toast.error("Program name + indication required");
      return;
    }
    setLoading(true); setDesignResults(null);
    try {
      const { data } = await api.post("/therapeutics/designation-eligibility", { ...df, designations });
      setDesignResults(data.results || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Screening failed");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="therapeutics-root" className="max-w-7xl">
      <div className="mb-6">
        <div className="overline mb-2 text-[#2563EB]">THERAPEUTICS INTELLIGENCE</div>
        <h1 className="font-serif text-3xl font-semibold tracking-tight lg:text-4xl">
          Orange Book, Purple Book, and FDA designations.
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-slate-600">
          Live openFDA lookups + AI-powered designation eligibility for Orphan, Breakthrough, Fast Track, Priority Review, and RMAT.
        </p>
      </div>

      {/* Mode tabs */}
      <div className="mb-6 grid gap-2 md:grid-cols-4">
        {TABS.map((t) => {
          const I = t.icon;
          const active = mode === t.code;
          return (
            <button
              key={t.code}
              data-testid={`therapeutics-mode-${t.code}`}
              onClick={() => { setMode(t.code); setRows([]); setTotal(0); setDesignResults(null); }}
              className={`flex items-center gap-3 border p-3 text-left transition-all ${
                active ? "border-[#2563EB] bg-[#2563EB]/5" : "border-slate-200 bg-white hover:border-slate-300"
              }`}
            >
              <I className={`h-5 w-5 ${active ? "text-[#2563EB]" : "text-slate-500"}`} strokeWidth={1.5} />
              <div>
                <div className={`text-sm font-medium ${active ? "text-slate-900" : "text-slate-700"}`}>{t.label}</div>
                <div className="text-[11px] text-slate-500">{t.hint}</div>
              </div>
            </button>
          );
        })}
      </div>

      {mode !== "design" && mode !== "label" && (
        <div className="mb-4 flex gap-3">
          <div className="flex flex-1 items-center border border-slate-200 bg-white pl-4">
            <Search className="h-4 w-4 text-slate-400" />
            <input
              data-testid="therapeutics-q"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && runSearch()}
              placeholder={mode === "orange" ? "brand or INN (e.g. aspirin, ANDA206392, warfarin)" : "biologic brand or INN (e.g. Humira, adalimumab)"}
              className="flex-1 border-none bg-transparent px-3 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none"
            />
          </div>
          <button
            data-testid="therapeutics-search-btn"
            onClick={runSearch}
            disabled={loading}
            className="inline-flex items-center gap-2 bg-[#2563EB] px-5 py-3 text-sm font-semibold text-white hover:bg-[#1D4ED8] disabled:opacity-50"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            Search
          </button>
        </div>
      )}

      {mode === "orange" && rows.length > 0 && (
        <div data-testid="orange-results" className="border border-slate-200 bg-white">
          <div className="border-b border-slate-200 bg-slate-50 px-4 py-2 text-xs">
            <span className="overline text-slate-500">ORANGE BOOK · {total.toLocaleString()} matches · showing {rows.length}</span>
          </div>
          {rows.map((r) => (
            <div key={r.application_number} className="border-b border-slate-100 p-4 text-sm last:border-b-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="border border-[#2563EB]/30 bg-[#2563EB]/5 px-1.5 py-0.5 font-mono text-[11px] text-[#1D4ED8]">{r.application_number}</span>
                <span className="font-semibold text-slate-900">{r.brand_name || r.active_ingredients?.[0] || "—"}</span>
                {r.reference_drug && <span className="border border-emerald-300 bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">RLD</span>}
                {r.reference_standard && <span className="border border-sky-300 bg-sky-50 px-1.5 py-0.5 text-[10px] font-semibold text-sky-700">Reference Standard</span>}
                {r.te_code && <span className="border border-slate-300 bg-slate-100 px-1.5 py-0.5 text-[10px] font-mono text-slate-700">TE: {r.te_code}</span>}
              </div>
              <div className="mt-1.5 text-xs text-slate-600">
                <b>{r.sponsor_name}</b> · {r.route || "—"} · {r.dosage_form || "—"} · {r.marketing_status || "—"}
              </div>
              {r.active_ingredients?.length > 0 && (
                <div className="mt-1 text-xs text-slate-500">Ingredients: {r.active_ingredients.join(" + ")}</div>
              )}
            </div>
          ))}
        </div>
      )}

      {mode === "purple" && rows.length > 0 && (
        <div data-testid="purple-results" className="border border-slate-200 bg-white">
          <div className="border-b border-slate-200 bg-slate-50 px-4 py-2 text-xs">
            <span className="overline text-slate-500">PURPLE BOOK · {total.toLocaleString()} biologic applications</span>
          </div>
          {rows.map((r) => (
            <div key={r.application_number} className="border-b border-slate-100 p-4 text-sm last:border-b-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="border border-purple-300 bg-purple-50 px-1.5 py-0.5 font-mono text-[11px] text-purple-800">{r.application_number}</span>
                <span className="font-semibold text-slate-900">{r.brand_name || r.proper_name}</span>
                {r.biosimilar_flag && <span className="border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">Biosimilar</span>}
              </div>
              <div className="mt-1.5 text-xs text-slate-600">
                <b>{r.sponsor_name}</b> · {r.route || "—"} · {r.dosage_form || "—"} · Proper name: {r.proper_name}
              </div>
              {r.first_approval && (
                <div className="mt-1 text-xs text-slate-500">First approval: {r.first_approval}</div>
              )}
            </div>
          ))}
        </div>
      )}

      {mode === "design" && (
        <div className="grid gap-6 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="space-y-3 border border-slate-200 bg-white p-5">
              <Field label="Program name *" testid="design-program" value={df.program_name} onChange={(e) => setDf({...df, program_name: e.target.value})} />
              <Field label="Indication *" testid="design-indication" value={df.indication} onChange={(e) => setDf({...df, indication: e.target.value})} />
              <Field label="Unmet medical need" value={df.unmet_need} onChange={(e) => setDf({...df, unmet_need: e.target.value})} />
              <Field label="US prevalence" value={df.prevalence} onChange={(e) => setDf({...df, prevalence: e.target.value})} placeholder="e.g., 40,000 US patients" />
              <Field label="Mechanism of action" value={df.mechanism} onChange={(e) => setDf({...df, mechanism: e.target.value})} />
              <Field label="Current evidence" value={df.preclinical_or_clinical} onChange={(e) => setDf({...df, preclinical_or_clinical: e.target.value})} placeholder="preclinical / Phase 1 signal / etc." />

              <div>
                <div className="overline mb-2 text-slate-500">Screen for</div>
                <div className="grid grid-cols-2 gap-2">
                  {DESIGS.map((d) => (
                    <label key={d.key} className="flex items-center gap-2 text-xs text-slate-700">
                      <input
                        type="checkbox"
                        checked={designations.includes(d.key)}
                        onChange={(e) => setDesignations(e.target.checked ? [...designations, d.key] : designations.filter((k) => k !== d.key))}
                        className="accent-[#2563EB]"
                      />
                      {d.label}
                    </label>
                  ))}
                </div>
              </div>

              <button
                data-testid="design-run"
                onClick={runDesignation}
                disabled={loading}
                className="mt-2 inline-flex w-full items-center justify-center gap-2 bg-[#2563EB] px-5 py-3 text-sm font-semibold text-white hover:bg-[#1D4ED8] disabled:opacity-40"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                Run eligibility screen
              </button>
            </div>
          </div>

          <div className="lg:col-span-7">
            {!designResults ? (
              <div className="flex h-64 flex-col items-center justify-center border border-dashed border-slate-200 bg-white text-center text-slate-500">
                <Award className="mb-3 h-6 w-6 text-[#2563EB]" strokeWidth={1.5} />
                <div className="text-sm">Fill the form, hit <b>Run eligibility screen</b>.</div>
                <div className="mt-1 text-xs text-slate-400">Verdict + rationale + missing evidence per designation.</div>
              </div>
            ) : (
              <div data-testid="design-results" className="space-y-3">
                {designResults.map((r) => (
                  <div key={r.designation} className="border border-slate-200 bg-white p-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="font-serif text-lg font-semibold text-slate-900">{r.designation}</div>
                      <span className={`border ${VERDICT_COLORS[r.verdict] || VERDICT_COLORS.not_eligible} px-2 py-0.5 text-[10px] font-semibold uppercase`}>{r.verdict}</span>
                      <span className="ml-auto font-mono text-xs text-slate-500">confidence {r.confidence}</span>
                    </div>
                    <p className="mt-2 text-sm text-slate-700">{r.rationale}</p>
                    {r.missing_evidence?.length > 0 && (
                      <div className="mt-2 text-xs">
                        <div className="overline mb-1 text-amber-600">Missing evidence</div>
                        <ul className="list-disc space-y-0.5 pl-4 text-slate-600">
                          {r.missing_evidence.map((m, i) => <li key={i}>{m}</li>)}
                        </ul>
                      </div>
                    )}
                    {r.citations?.length > 0 && (
                      <div className="mt-2 text-[11px] text-slate-500">
                        {r.citations.join(" · ")}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {mode === "label" && (
        <LabelLookupPanel onAnchor={onAnchor} />
      )}
    </div>
  );
}

function Field({ label, testid, ...p }) {
  return (
    <label className="block">
      <div className="overline mb-1 text-slate-500">{label}</div>
      <input data-testid={testid} {...p} className="w-full border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900" />
    </label>
  );
}
